// src/api/reports.ts
//
// Frontend API client for Reports (LLM-authored HTML reports with live data).
// Phase 1 — CRUD + version listing. Phase 2 Spoor B1 adds hook resolver
// (ArtifactFrame → parent bridge → this client). Authoring, review,
// schedules ship in later phases.
//
// See plan: docs/plans-pending/2026-04-21-reports-feature.md
import type { HookCallPayload, HookResolveResponseBody, ReportBrandInit } from "@verity/shared-types";
import type { components } from "@verity/shared-types";
import type { ReportAudience, ReportPurpose } from "@/constants/reportBrief";
import { apiJson, apiNoContent } from "./client";
import type { DashboardDTO } from "./dashboards";
import type { ChartColorPreset } from "./preferences";

// ==========================================
// TYPES
// ==========================================

export type ReviewStatus = "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "REJECTED";
export type VersionStatus = "building" | "complete" | "failed";

/** Background-build state of a report (plan 2026-08-23 report-creation-
 * background). NULL on every report that was never built through the
 * background path — the absence of the field is not a failure. */
export type ReportBuildStatus = "QUEUED" | "RUNNING" | "READY" | "FAILED";

/** Closed enum the backend books on a build. The frontend maps it to fixed
 * customer copy (see `reportBuildCopy`) — the backend never sends exception
 * text, so an unknown value must degrade to the generic message rather than
 * be rendered. */
export type ReportBuildErrorKind =
  | "enqueue_failed"
  | "enqueue_lost"
  | "orphaned"
  | "retry_exhausted"
  | "lock_held"
  | "author_no_longer_member"
  | "build_failed"
  | "narrative_failed"
  /** READY, nothing failed, and some readings have a gap in them: the model's
   *  answer was mostly placeable and one update was not, so a block's text is
   *  written except for one sentence. A separate kind because the banner
   *  renders on the kind, so folding this into "ready" would leave the
   *  customer un-told and Try again un-offered. */
  | "narrative_partial"
  /** The readings were not written because the report is submitted or
   *  approved. Refused BEFORE any model call (Arjen, 2026-09-26): the
   *  synchronous half already answers 409 here, and the background half used
   *  to do the work and then refuse to advance the pointer, which cost a
   *  dollar for a version nobody could reach. */
  | "not_draft";

/** Sections written / to write of the in-flight build. Detail endpoint only
 * (the list would N+1 on it). */
export type ReportBuildProgress = {
  sections_done: number;
  sections_total: number;
};

/** Kept in step with `packages/shared-types/src/reports.ts::ReportSectionShape`
 * (ADR-0018 §8). Nothing branches on the value at runtime — it is a skeleton
 * hint — so an unknown shape is inert, but the union must stay wide enough to
 * type what the SSE stream actually delivers AND what older stored outlines
 * hold. It is therefore wider than the tool's enum on purpose: `comparison`
 * was withdrawn there on 2026-09-06 and still exists in stored data. */
export type ReportSectionShape =
  | "chart"
  | "text"
  | "kpi_grid"
  | "table"
  | "comparison"
  | "header"
  | "exec_summary"
  | "recommendations";

export type ReportOutlineSection = {
  id: string;
  title: string;
  description?: string;
  shape?: ReportSectionShape;
};

export type ReportDTO = {
  public_id: string;
  workspace_public_id: string;
  title: string;
  description: string | null;
  /** Registry template this report was created from, or null for
   * "start from scratch". Drives the schedule cadence prefill; an unknown
   * key (retired template) must degrade to no prefill, never an error. */
  template_key: string | null;
  /** BLOCK-template provenance. `template_version` is the registry version
   * this report was instantiated from; `template_state` is the server's
   * verdict on it and the ONLY thing the UI may act on:
   *
   *   `current`           the report matches the shipped definition
   *   `update_available`  a newer version exists and the report is untouched
   *   `customised`        a newer version exists AND the report was edited,
   *                       so taking it discards those edits
   *   `retired`           the template is gone from the registry
   *
   * Both are null for a narrative template and for a report built by hand.
   * Never infer the state from `template_version` client-side: the server
   * compares a DIGEST, not a number. */
  template_version?: number | null;
  template_state?: "current" | "update_available" | "customised" | "retired" | null;
  /** True when the report is BUILT FROM BLOCKS (plan 2026-09-02
   * fill-with-chat-report-blocks): its content is the block recipe on
   * `/reports/:id/blocks`, not a template or an authoring instruction.
   * Optional on the wire so a payload that predates the column reads as
   * `false` rather than breaking the page — `/iterate` and the Edit entry
   * are withheld on a composed report (the backend answers 409). */
  composed?: boolean;
  /** The retired program report (ADR-0495): not built from blocks and not an
   * Analyst edition. Read-only everywhere — the server refuses every edit,
   * review step and schedule change with 409 `report_kind_retired`. Read
   * this flag; never re-derive it from `composed` and `cadence`. Optional on
   * the wire so an older payload reads as not retired. */
  kind_retired?: boolean;
  /** The one window a composed report renders, as a period preset
   * (`last_month`, `this_quarter`, …). Null for every other report. */
  period?: string | null;
  /** Cadence of the standing analyst report this is an edition of, or null
   * for every hand-authored report. Lets the list and the reading surface
   * name a machine document instead of showing it as an unexplained draft. */
  cadence: string | null;
  /** Public id of the user who created the report — used by ReportView to
   * decide whether to show admin-or-author UI (Iterate, Compare, History). */
  author_public_id: string;
  review_status: ReviewStatus;
  rejection_reason: string | null;
  current_version_number: number | null;
  approved_version_number: number | null;
  revision: number;
  /** `portrait` | `landscape` on a page report, null/absent on a flowing one
   * (plan 2026-09-06-report-page-formats). This is the EDITOR's switch: a
   * page report is edited on the stage itself (`ReportEditor`), a flowing one on
   * `ReportBlocksPanel`, and never both. Optional on the wire so a payload
   * that predates pages reads as "flowing", which is what it is. */
  page_format?: string | null;
  /** The report's DEFAULT comparison (plan 2026-09-13-report-builder-parity-
   *  workstreams, W1; ADR-0267): a block without its own `compare` inherits
   *  a shifted mode from it; a template report is built with it. Optional on
   *  the wire so an older payload reads as "none". */
  compare_mode?: "previous_period" | "same_period_last_year" | "target" | null;
  /** "Edited since the last build" (plan 2026-09-08-report-editor-is-the-
   *  report-itself, Besluit 9): the revision the current version was built
   *  from, and the server's comparison. Both optional so a client that
   *  predates them is unaffected. */
  built_revision?: number | null;
  recipe_changed?: boolean;
  /** True when the caller only sees this report because it was shared with
   * them (or with one of their workspaces) — never set for account admins,
   * who see everything through the admin bypass. Presentation-only: the
   * backend has already decided access, this just lets the list say why the
   * row is there. Defaults to false on older payloads. */
  shared_with_me?: boolean;
  /** Background build state. Absent/null on reports that predate the
   * background path or were never built through it. */
  build_status?: ReportBuildStatus | null;
  /** Why the build failed, or `narrative_failed` alongside READY when the
   * report is usable but its summary could not be written. */
  build_error_kind?: ReportBuildErrorKind | null;
  /** How much of the last build's writing landed: how many sections got a
   *  narrative pass, and how many of those could not be written. Both absent on
   *  a report no background build has reported on, which is why the copy still
   *  has a wordy fallback. */
  narrative_total?: number | null;
  narrative_missing?: number | null;
  /** Readings that WERE written and have a gap in them. A third outcome, not
   *  a subset of the two above. */
  narrative_partial?: number | null;
  /** How many readings the one-pass retry could actually rewrite.
   *
   *  NOT the same as `narrative_missing`, and gating on the wrong one is a
   *  button that offers something the server refuses: the count has been
   *  written since 2026-09-27, the set the retry needs only since 09-29, so
   *  every report built before then has a count and nothing to target. */
  narrative_retryable?: number | null;
  /** GET /reports/:id only — never on list rows. */
  build_progress?: ReportBuildProgress | null;
  // --- Report brief (plan 2026-08-28 report-brief-audience-purpose-sources) ---
  /** Who this report is written for. `null` on every report created before
   * the brief existed — that is a pre-brief report, not a failure. Narrow
   * with `asReportAudience` before using it as a key: a retired key must
   * degrade to "not chosen", never round-trip into a 422. */
  brief_audience?: string | null;
  /** What the report has to do. Same contract as `brief_audience`. */
  brief_purpose?: string | null;
  /** TRUE size of the report's explicit source selection; `null` = "Auto"
   * (Verity picks). This is the number to display — it counts sources the
   * reader may not be allowed to see. */
  brief_source_count?: number | null;
  /** The subset of that selection THIS reader has a grant on. Deliberately
   * shorter than `brief_source_count` when the reader lacks grants on part
   * of it (the `partition_for_chat` doctrine: never enumerate sources a
   * reader has no access to). NEVER render this as the full selection, and
   * never PATCH it back as one — doing so would silently drop the
   * sources the reader cannot see. */
  brief_source_public_ids?: string[] | null;
  /** The lens this report reads the analyst through (plan 2026-10-13, B4).
   * `null` = the platform lens of its `brief_audience` × `cadence`. Only an
   * ACTIVE account lens or a lens of the report's own workspace is ever
   * bound; resolve it against `listReportLenses` (`@/api/reportLenses`). */
  lens_public_id?: string | null;
  /** The author's extra emphasis for this report's narrative (≤ 500,
   * stored sanitised). `null` = none. */
  brief_focus_text?: string | null;
  /** The report's own chart palette as stored (plan 2026-09-26-report-charts-
   * follow-the-account-palette). `null` preset = inherits the account's. */
  chart_color_preset?: ChartColorPreset | null;
  chart_custom_colors?: string[] | null;
  /** The palette its charts are drawn with: report > account > platform,
   * lowercase `#rrggbb`. `null` on list rows (no list draws a chart). */
  chart_palette?: string[] | null;
  chart_palette_source?: "report" | "account" | "platform" | null;
  created_at: string;
  updated_at: string;
};

/** One row of the paginated version list — metadata only.
 *
 * `outline` and `hook_dependencies` are deliberately NOT here: they are
 * per-version detail served by the render/diff endpoints, and shipping them
 * per row made the list payload grow with a machine cadence's ~2 versions
 * per period (analysis-document step 4).
 *
 * `period_*` is the analysed period, null for hand-authored versions;
 * `is_machine` marks a spec-backed (analyst-written) edition. */
export type ReportVersionDTO = {
  version_number: number;
  status: VersionStatus;
  created_at: string;
  period_start: string | null;
  period_end: string | null;
  is_machine: boolean;
};

export type ReportVersionListResponse = {
  items: ReportVersionDTO[];
  total: number;
  has_more: boolean;
  total_incomplete_hidden: number;
};

// ==========================================
// CRUD
// ==========================================

export async function listReports(params?: {
  workspace_public_id?: string;
  /** Comma-separated `ReportBuildStatus` values, e.g. "QUEUED,RUNNING".
   * Used by the build-context bootstrap poll to resume tracking after a
   * reload; the backend parses it to the closed enum (422 on anything
   * else) and the BFF forwards it explicitly. */
  build_status?: string;
}): Promise<{ reports: ReportDTO[] }> {
  const search = new URLSearchParams();
  if (params?.workspace_public_id) {
    search.set("workspace_public_id", params.workspace_public_id);
  }
  if (params?.build_status) search.set("build_status", params.build_status);
  const qs = search.toString();
  return apiJson(`/reports${qs ? `?${qs}` : ""}`);
}

export async function getReport(publicId: string): Promise<ReportDTO> {
  return apiJson(`/reports/${encodeURIComponent(publicId)}`);
}

export async function createReport(body: {
  workspace_public_id: string;
  title: string;
  description?: string;
  /** Registry key when the report starts from a template. The backend
   * validates it against the registry, so an unknown key is a 422 rather
   * than a silently stored string. Omitted for "start from scratch". */
  template_key?: string;
  /** Create the report as a composition of blocks. Every report started from
   * the New report modal is one of these: with a `template_key` of a BLOCK
   * template the server instantiates that recipe, without one it is an empty
   * report. `period` is required unless a block template supplies it. Since
   * 2026-09-26 this needs no capability; it is a workspace-EDITOR action like
   * any other create. Since ADR-0495 it is the only kind made: a create
   * without it is refused with 409 `report_kind_retired`. */
  composed: true;
  /** The composed report's window, a period preset. Required with
   * `composed`; the backend validates it against the preset table. */
  period?: string;
  /** The page format of a composed report (plan 2026-09-08-report-editor-
   * is-the-report-itself, A1/C9). Omitted = portrait. */
  page_format?: "portrait" | "landscape";
  /** Where the create came from — operator logs only, no behaviour. */
  origin?: "ui" | "assistant";
  /** What this report compares its numbers against. Omit to use the
   * template's own choice — that is what every report did before this
   * setting existed, so omitting it is the default, not a gap.
   *
   * `"target"` is NOT a value here: targets do not exist yet. The backend
   * rejects it, so a client that sends it anyway fails loudly rather than
   * silently comparing to something else. */
  compare_mode?: "previous_period" | "same_period_last_year" | "target";
  /** Report brief (plan 2026-08-28): who the report is for. Optional on the
   * wire so older clients keep working during the expand/contract rollout —
   * the drawer nonetheless requires it, because a brief nobody fills in is
   * not a brief. Keys come from `api/constants/report_brief.py`. */
  audience?: ReportAudience;
  /** Report brief: what the report has to do. Same contract as `audience`. */
  purpose?: ReportPurpose;
  /** Sources the report may draw on. OMITTED (never `[]`) for "Auto" — an
   * empty list and an absent field mean the same thing to the backend, and
   * sending one of them consistently keeps the payload readable. Unknown or
   * foreign ids are a 400, so this must carry ids the account owns. */
  source_public_ids?: string[];
}): Promise<DashboardDTO> {
  // A report is born as a dashboard of kind `report` (ADR-0496 D1, plan
  // 2026-10-22 step A5): the answer is that dashboard.
  return apiJson(`/reports`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateReport(
  publicId: string,
  body: {
    title?: string;
    /** The report's default comparison (W1.7; ADR-0267). `null` clears it;
     *  a block without its own choice then compares against nothing. */
    compare_mode?: "previous_period" | "same_period_last_year" | null;
    /** The report's one window (W2.2; ADR-0267): a catalogue preset or a
     *  rolling spelling (`rolling_4_weeks`). Only a report built from blocks
     *  has one (409 otherwise); refused while a build is in flight (409) and
     *  when it is not a period (422 naming the choices). */
    period?: string;
    description?: string | null;
    /** Report brief (plan 2026-08-28). NOTE the field names: the wire
     * contract is `audience` / `purpose` / `source_public_ids`, while the
     * DTO reads them back as `brief_*`. The route handles these three
     * EXPLICITLY (not through its generic setattr loop) precisely because
     * the names differ — sending `brief_audience` here would be accepted
     * with a 200 and persist nothing. */
    audience?: ReportAudience;
    purpose?: ReportPurpose;
    /** The complete selection. `[]` is normalised to NULL server-side and
     * means "Auto" — there is no separate way to spell it. Only send this
     * when the caller can see the WHOLE selection (see
     * `ReportDTO.brief_source_public_ids`); a partial list would delete
     * the sources the caller was not allowed to see.
     *
     * Answers 409 while a build is in flight, and 400 when the selection
     * names a source that is not available to the account. */
    source_public_ids?: string[];
    /** The report's own chart palette. `null` = inherit the account's (and
     * clears custom colours). `custom` needs `chart_custom_colors` (2-20 hex,
     * sent or already stored); colours beside any other preset are a 400.
     * Only while the report is a DRAFT: 409 otherwise. */
    chart_color_preset?: ChartColorPreset | null;
    chart_custom_colors?: string[];
    /** Bind a lens (plan 2026-10-13, B4): an ACTIVE account lens or a lens of
     * the report's own workspace. `null` unbinds — the report reads through
     * the platform lens of its audience × cadence again. A pending proposal
     * is a 409 `lens_pending`; approve it through `approveReportLens`. */
    lens_public_id?: string | null;
    /** Extra emphasis for the narrative, ≤ 500 characters; `null` clears it. */
    brief_focus_text?: string | null;
    /** Optional optimistic-concurrency token matching ReportDTO.revision. */
    if_match?: number;
  },
): Promise<ReportDTO> {
  return apiJson(`/reports/${encodeURIComponent(publicId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteReport(publicId: string): Promise<void> {
  return apiNoContent(`/reports/${encodeURIComponent(publicId)}`, {
    method: "DELETE",
  });
}

export async function listReportVersions(
  publicId: string,
  options?: {
    include_incomplete?: boolean;
    limit?: number;
    offset?: number;
    /** Pin this version into the page even when it falls outside the
     * window — a deep link `?version=N` must resolve its label. */
    include_version?: number;
  },
): Promise<ReportVersionListResponse> {
  const qs = new URLSearchParams();
  if (options?.include_incomplete) qs.set("include_incomplete", "true");
  if (options?.limit != null) qs.set("limit", String(options.limit));
  if (options?.offset != null) qs.set("offset", String(options.offset));
  if (options?.include_version != null) {
    qs.set("include_version", String(options.include_version));
  }
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiJson(`/reports/${encodeURIComponent(publicId)}/versions${suffix}`);
}

/** One finding of an analysis edition, for the host-rendered findings rail.
 * Mirrors the backend's embedded shape — `direction` does not exist there,
 * it is folded into `sentiment` by the scorer. */
export type ReportFindingDTO = {
  finding_id: string;
  metric_key: string | null;
  dataset_key: string | null;
  metric_label: string | null;
  dimension_value: string | null;
  sentiment: string | null;
  block_slot: string | null;
  source_name: string | null;
};

export async function listVersionFindings(
  publicId: string,
  versionNumber: number,
): Promise<{ version_number: number; findings: ReportFindingDTO[] }> {
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/versions/${versionNumber}/findings`,
  );
}

// ==========================================
// TEMPLATES (the New report modal)
// ==========================================

/** One card in the New report modal, from the BLOCK template registry.
 *
 * `available` is per CALLER, not per account: the backend computes it from the
 * same grant-filtered catalogue read the create itself performs, so an offered
 * card cannot be refused by the create and a greyed card would not have
 * worked. `missing_packs` carries pack LABELS ("Ecom Performance"), ready to
 * show, because the customer reads them. */
export type BlockTemplateCardDTO = {
  key: string;
  version: number;
  label: string;
  description: string;
  period: string;
  page_format: "portrait" | "landscape";
  compare_mode: string | null;
  audience: string;
  available: boolean;
  missing_packs: string[];
};

export async function listReportTemplates(): Promise<{
  block_templates: BlockTemplateCardDTO[];
}> {
  return apiJson(`/reports/templates`);
}

/** One pre-resolved hook call for ArtifactFrame's preview mode. `args` is the
 * raw args object exactly as the sandbox will request it — the preview
 * resolver stringifies both sides itself at match time. (Its one caller, the
 * template gallery preview, went with the program report, ADR-0495.) */
export type TemplatePreviewHookEntry = {
  hook_name: string;
  args: Record<string, unknown>;
  data: unknown;
  provenance?: unknown;
};

// ==========================================
// PROVENANCE + SQL (ADR-0018)
// ==========================================

export type ProvenanceTimeframe = {
  start: string;
  end: string;
  tz: string;
};

/** The window a hook compared against and which comparison it was; `mode`
 * is absent on provenance stored before W1.2. */
export type ProvenanceComparisonPeriod = ProvenanceTimeframe & {
  mode?: "previous_period" | "same_period_last_year" | "target" | null;
};

export type ProvenanceFilter = {
  dimension: string;
  operator: string;
  value_label: string;
};

export type SectionProvenance = {
  timeframe: ProvenanceTimeframe;
  comparison_period: ProvenanceComparisonPeriod | null;
  sources: string[];
  datasets: string[];
  metrics: string[];
  dimensions: string[];
  filters: ProvenanceFilter[];
  query_timestamp: string;
  partial: boolean;
  failed_hooks: string[];
};

export type SectionProvenanceResponse = {
  section_id: string;
  version: number;
  provenance: SectionProvenance;
};

export type SectionSqlResponse = {
  section_id: string;
  version: number;
  sql: string;
};

/**
 * ADR-0018 §9: host-side fetch of section provenance. The iframe only
 * signals "open the drawer for section X"; the real data comes from here
 * with the user's JWT, not from iframe payload.
 */
export async function getSectionProvenance(
  publicId: string,
  sectionId: string,
  options?: { version?: number },
): Promise<SectionProvenanceResponse> {
  const qs = new URLSearchParams();
  if (options?.version) qs.set("version", String(options.version));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/sections/${encodeURIComponent(
      sectionId,
    )}/provenance${suffix}`,
  );
}

/**
 * ADR-0018 §5: regenerated SQL. Gated server-side on AccountRole ∈
 * {OWNER, ADMIN}; lower-role callers get a generic 403. Frontend should
 * hide the "Show SQL" button for other roles, but the backend is the
 * authoritative gate.
 */
export async function getSectionSql(
  publicId: string,
  sectionId: string,
  options?: { version?: number },
): Promise<SectionSqlResponse> {
  const qs = new URLSearchParams();
  if (options?.version) qs.set("version", String(options.version));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/sections/${encodeURIComponent(
      sectionId,
    )}/sql${suffix}`,
  );
}

// ==========================================
// RENDER (Phase 2 Spoor B4)
// ==========================================

export type ReportTheme = "light" | "dark";

/** Present only when the rendered version is a spec-backed analysis edition.
 * `periodic_analysis_public_id` is nullable on purpose: the provenance FK is
 * ON DELETE SET NULL, so a purged analysis still leaves a readable document. */
export type ReportRenderAnalysis = {
  period_start: string;
  period_end: string;
  narrative_status: string | null;
  periodic_analysis_public_id: string | null;
};

/** One declared hook call of the rendered version, in the wire shape the
 * sandbox sends (`metric_id` / `dimension_id` for the id-first hooks). The
 * parent forwards the list unchanged in `verity:init`; the sandbox
 * pre-issues every entry before the first render so one resolve batch
 * replaces one chained round trip per hook depth. */
export type RenderHookCall = {
  hook_name: string;
  args: Record<string, unknown>;
};

export type ReportRenderPayload = {
  version_number: number | null;
  status: VersionStatus | null;
  outline: ReportOutlineSection[];
  compiled_js: string | null;
  theme: ReportTheme;
  /** `portrait` | `landscape` on a page-format report, `null` on a flowing
   *  one. The outline entries carry the per-section cells; this is the page
   *  box they sit in. Nullable rather than absent so the two states are
   *  distinguishable without checking `undefined`. */
  page_format: string | null;
  /** Page furniture of a page report (plan 2026-09-08-report-editor-is-the-
   *  report-itself, A6): the title the header band shows and the pages the
   *  contents list, `[{index, label}]` in reading order. Absent on a flowing
   *  report. The sandbox draws furniture only when `title` is present. */
  title?: string | null;
  pages?: { index: number; label: string; key?: string }[] | null;
  /** What the cover sheet says besides the title (`ReportCoverDTO`, plan
   *  2026-09-19 phase E). Server-derived on purpose: the period is the
   *  report's own, resolved at the EDITION's build time in the account's
   *  timezone, which no client can reproduce and one got visibly wrong. */
  cover?: {
    brand_name?: string | null;
    period_start?: string | null;
    period_end?: string | null;
    issued_at?: string | null;
  } | null;
  /** The tenant's brand, resolved server-side (plan 2026-09-13, ADR-0264).
   *  Absent on a template/JSX report and on every account that set no brand;
   *  the surfaces then emit nothing and the document is what it was. */
  brand?: ReportBrandInit | null;
  /** The chart palette, on EVERY report (not only composed ones): report >
   *  account > platform, lowercase `#rrggbb`. Absent from a backend that
   *  predates it; the drawing layer then keeps its token palette. */
  chart_palette?: string[] | null;
  /** Each page's backdrop as the rendered version recorded it (plan
   *  2026-10-09 dashboard shapes, D3), raw: the drawing package coerces it. */
  backdrops?: Record<string, unknown> | null;
  is_approved: boolean;
  analysis: ReportRenderAnalysis | null;
  /** Empty when there is no version or when the version predates stored
   * hook args; capped server-side below the resolver's batch budget. */
  hook_calls: RenderHookCall[];
  /** The version drawn from DATA (fase 3 of ADR-0257): present only when EVERY
   *  section of it can be expressed without running its program, `null`
   *  otherwise with the reason in the render log. It carries the two things
   *  that live on the section rather than on the outline entry — the call each
   *  block makes and the words the narrative pass wrote — and joins to the
   *  outline on `section_id` for everything else, so no fact travels twice. */
  block_document?: {
    sections: {
      section_id: string;
      blocks: {
        key: string;
        /** Every call this block makes, each under the SLOT its answer fills:
         *  `main` (the block's own), `subtitle` (a chart or table subtitle's
         *  values), `mtd` / `ytd` (the anchors). Empty for a block with none.
         *  `query_weight` is the resolver's own upper-bound estimate: the
         *  reading room cuts its batches on it, because the resolver REFUSES
         *  past its budget rather than degrading. */
        hooks?: (RenderHookCall & { slot: string; query_weight?: number | null })[];
        /** `[{component, occurrence, fields}]` — the analyst text of this
         *  block, in emit order. */
        narrative?: Record<string, unknown>[] | null;
      }[];
    }[];
  } | null;
};

/** Fetch the renderable version bundle for a report. Returns a payload whose
 * `compiled_js` is `null` when the report has no version yet (fresh draft,
 * LLM authoring not yet run).
 *
 * Phase 5: pass a specific `version` to render that version's bundle
 * instead of the default (approved → current). Non-complete versions
 * require admin-or-author on the backend — the UI shows only the pointer
 * selector for those roles. */
export async function renderReport(
  publicId: string,
  version?: number,
): Promise<ReportRenderPayload> {
  const suffix = version ? `?version=${encodeURIComponent(String(version))}` : "";
  return apiJson(`/reports/${encodeURIComponent(publicId)}/render${suffix}`);
}

// ==========================================
// PHASE 5 — DIFF + HISTORY
// ==========================================

export type DiffKind =
  | "added"
  | "removed"
  | "modified"
  | "unchanged"
  | "source_unavailable";

export type SectionDiff = {
  section_id: string;
  diff_kind: DiffKind;
  unified_diff: string | null;
};

export type RenamedSection = {
  section_id: string;
  from_title: string;
  to_title: string;
};

export type ReshapedSection = {
  section_id: string;
  from_shape: string;
  to_shape: string;
};

export type OutlineChanges = {
  added: string[];
  removed: string[];
  renamed: RenamedSection[];
  reshaped: ReshapedSection[];
};

export type ReportVersionDiffResponse = {
  from_version: number;
  to_version: number;
  outline_changes: OutlineChanges;
  sections: SectionDiff[];
};

/** Fetch source-level diff between two versions (admin-or-author only).
 * Backend tolerates `from > to` and swaps silently; caller doesn't need
 * to normalise. */
export async function getVersionDiff(
  publicId: string,
  from: number,
  to: number,
): Promise<ReportVersionDiffResponse> {
  const qs = new URLSearchParams({
    from: String(from),
    to: String(to),
  });
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/diff?${qs.toString()}`,
  );
}

export type EventRole = "user" | "assistant" | "tool";

export type HistoryEventContent = {
  instruction?: string;
  message?: string;
  section_id?: string;
  compiled_js_size_bytes?: number;
  position?: number;
  old_version_number?: number;
  new_version_number?: number;
};

export type HistoryEventDTO = {
  sequence: number;
  role: EventRole;
  tool_name: string | null;
  content: HistoryEventContent;
  created_at: string;
};

/** List authoring events for a specific version (admin, author, or
 * workspace OWNER only). */
export async function getVersionEvents(
  publicId: string,
  versionNumber: number,
): Promise<{ events: HistoryEventDTO[] }> {
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/versions/${encodeURIComponent(String(versionNumber))}/events`,
  );
}

/** How a single authoring turn ended. `no_op` = the run finished cleanly
 * but rewrote nothing; `failed` = the run never produced a version. */
export type TurnOutcome = "complete" | "failed" | "no_op";

export type AuthoringTurnDTO = {
  instruction: string;
  created_at: string;
  outcome: TurnOutcome;
  /** Version the turn produced — only set for `complete`. */
  resulting_version_number: number | null;
  /** Distinct sections rewritten by the turn. */
  edited_section_count: number;
  last_explain: string | null;
  author_public_id: string | null;
  /** Display name only — the backend never returns an email here. */
  author_name: string | null;
};

export type AuthoringTurnsResponse = {
  /** Oldest first. */
  turns: AuthoringTurnDTO[];
};

/** Cross-session authoring history for the iterate panel (admin, author,
 * or workspace OWNER only — a non-author workspace EDITOR gets a 403,
 * which callers treat as "no history", not an error). */
export async function getAuthoringTurns(
  publicId: string,
  options?: { limit?: number },
): Promise<AuthoringTurnsResponse> {
  const qs = new URLSearchParams();
  if (options?.limit !== undefined) qs.set("limit", String(options.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/authoring-turns${suffix}`,
  );
}

// ==========================================
// HOOK RESOLVER (Phase 2 Spoor B1)
// ==========================================

/**
 * Resolve a batch of hook calls for a report. Called by ArtifactFrame on
 * behalf of the sandboxed iframe — CSP `connect-src` means the iframe
 * cannot call the backend directly.
 *
 * The `X-Verity-Artifact-Id` header tags audit logs so per-artifact
 * rate-limits + usage analytics work. It is NOT used for authorisation —
 * the JWT cookie is. Backend enforces account + workspace scoping and
 * visibility-gates the report before resolving.
 */
export async function resolveHooks(
  publicId: string,
  hookCalls: HookCallPayload[],
): Promise<HookResolveResponseBody> {
  return apiJson<HookResolveResponseBody>(
    `/reports/${encodeURIComponent(publicId)}/hooks/resolve`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Verity-Artifact-Id": publicId,
      },
      body: JSON.stringify({ hook_calls: hookCalls }),
    },
  );
}

// ==========================================
// PHASE 6 — REVIEW & APPROVAL
// ==========================================

export type ReviewAction =
  | "submitted"
  | "approved"
  | "rejected"
  | "reverted_to_draft"
  | "superadmin_override"
  | "forced_revert"
  | "machine_approved";

export type ReviewEventDTO = {
  action: ReviewAction;
  actor_public_id: string;
  actor_name: string;
  from_status: ReviewStatus;
  to_status: ReviewStatus;
  note: string | null;
  created_at: string;
  version_number: number | null;
};

export type TransitionResponse = {
  report: ReportDTO;
  event: ReviewEventDTO;
};

export type ReviewQueueItemDTO = {
  public_id: string;
  title: string;
  workspace_public_id: string;
  workspace_name: string;
  author_public_id: string;
  author_name: string;
  submitted_at: string | null;
  current_version_number: number | null;
};

export type ReviewQueueResponse = {
  items: ReviewQueueItemDTO[];
  total: number;
  has_more: boolean;
};

/** Submit DRAFT → PENDING_REVIEW. */
export async function submitForReview(
  publicId: string,
  body: { if_match?: number } = {},
): Promise<TransitionResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/submit-for-review`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

/** Approve PENDING_REVIEW → APPROVED. `note` optional, included in
 * notification body and review-events timeline. */
export async function approveReport(
  publicId: string,
  body: { if_match?: number; note?: string } = {},
): Promise<TransitionResponse> {
  return apiJson(`/reports/${encodeURIComponent(publicId)}/approve`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Reject PENDING_REVIEW → REJECTED. `reason` required, min 20 chars. */
export async function rejectReport(
  publicId: string,
  body: { if_match?: number; reason: string },
): Promise<TransitionResponse> {
  return apiJson(`/reports/${encodeURIComponent(publicId)}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Revert any non-DRAFT report → DRAFT. `force=true` bypasses the active-
 * schedule guard; account OWNER only — backend returns 403 otherwise. */
export async function revertToDraft(
  publicId: string,
  body: { if_match?: number; force?: boolean } = {},
): Promise<TransitionResponse> {
  return apiJson(`/reports/${encodeURIComponent(publicId)}/revert-to-draft`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** List PENDING_REVIEW reports the caller can see (workspace-scoped for
 * non-admins; all-account for admins/OWNERs/SUPPORT). */
export async function listReviewQueue(params?: {
  workspace_public_id?: string;
  limit?: number;
  offset?: number;
  sort?: "submitted_asc" | "submitted_desc";
}): Promise<ReviewQueueResponse> {
  const qs = new URLSearchParams();
  if (params?.workspace_public_id)
    qs.set("workspace_public_id", params.workspace_public_id);
  if (params?.limit !== undefined) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));
  if (params?.sort) qs.set("sort", params.sort);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiJson(`/reports/review-queue${suffix}`);
}

/** Lightweight count for the sidebar badge. Polled every 60s. */
export async function getReviewQueueCount(): Promise<{ count: number }> {
  return apiJson(`/reports/review-queue/count`);
}

/** Audit timeline — all review-status transitions on a report. */
export async function listReviewEvents(
  publicId: string,
): Promise<{ events: ReviewEventDTO[] }> {
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/review-events`,
  );
}

// ==========================================
// PHASE 7 — LOGIN PREFETCH / PREWARM
// ==========================================

export type RecentReport = {
  public_id: string;
  approved_version_number: number;
};

/** List ≤20 reports the caller viewed in the last 30 days that have an
 * approved version — the source list for the login prewarm worker.
 * Workspace-visibility is re-checked at query time, so reports in
 * workspaces the user has been removed from do NOT appear. */
export async function getRecentReports(): Promise<{ reports: RecentReport[] }> {
  return apiJson(`/reports/viewed-recently`);
}

/** Trigger a body-less prewarm on a report's approved version. The backend
 * flattens `ReportVersionSection.hook_args_json` across sections and passes
 * the raw list to `hook_resolver.resolve_hooks` — which canonicalises and
 * writes the account-scoped cache using the EXACT same key the interactive
 * render path reads. Fire-and-forget: returns `{ enqueued: N }` where N is
 * the count of calls attempted (not successes). */
export async function prewarmReportHooks(
  publicId: string,
): Promise<{ enqueued: number }> {
  return apiJson(
    `/reports/${encodeURIComponent(publicId)}/hooks/prewarm`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

/** Record an explicit view impression on a report (without fetching the
 * bundle). Used by surfaces like landing-page cards that want to update
 * "last viewed" without triggering /render. */
export async function markReportViewed(publicId: string): Promise<void> {
  return apiNoContent(
    `/reports/${encodeURIComponent(publicId)}/view`,
    { method: "POST" },
  );
}

// ============================================================================
// Phase 8 — schedule CRUD + deliveries audit
// ============================================================================

import type {
  ReportScheduleDTO,
  ReportDeliveryDTO,
  ReportRecipientDirectoryResponse,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  DeliverNowResponse,
  SignedUrlResponse,
} from "@verity/shared-types";

export async function listSchedules(
  reportPublicId: string,
): Promise<{ schedules: ReportScheduleDTO[] }> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/schedules`);
}

/** The account members who may be picked as schedule recipients — exactly the
 * set the create/update endpoints accept, so the picker cannot offer an
 * address the write would then refuse.
 *
 * Gated server-side on `create_schedule`, not `read`: the response is the
 * account roster. Callers that only read a report will get a 403, and a report
 * that is not APPROVED yields a 409 (it cannot be scheduled either). */
export async function listRecipientDirectory(
  reportPublicId: string,
): Promise<ReportRecipientDirectoryResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/recipient-directory`,
  );
}

export async function createSchedule(
  reportPublicId: string,
  body: ScheduleCreateRequest,
): Promise<ReportScheduleDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/schedules`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateSchedule(
  reportPublicId: string,
  schedulePublicId: string,
  body: ScheduleUpdateRequest,
): Promise<ReportScheduleDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/schedules/${encodeURIComponent(schedulePublicId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export async function deleteSchedule(
  reportPublicId: string,
  schedulePublicId: string,
): Promise<void> {
  return apiNoContent(
    `/reports/${encodeURIComponent(reportPublicId)}/schedules/${encodeURIComponent(schedulePublicId)}`,
    { method: "DELETE" },
  );
}

export async function deliverScheduleNow(
  reportPublicId: string,
  schedulePublicId: string,
): Promise<DeliverNowResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/schedules/${encodeURIComponent(schedulePublicId)}/deliver-now`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function listDeliveries(
  reportPublicId: string,
): Promise<{ deliveries: ReportDeliveryDTO[] }> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/deliveries`);
}

export async function getDeliveryDownloadUrl(
  reportPublicId: string,
  deliveryPublicId: string,
): Promise<SignedUrlResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/deliveries/${encodeURIComponent(deliveryPublicId)}/download`,
  );
}

/** Mail a prepared edition. The backend claims the row before contacting the
 * mail provider, so a second call loses the race and 409s — never a second
 * copy in the recipients' inboxes. Returns the delivery in its final state. */
export async function sendPreparedDelivery(
  reportPublicId: string,
  deliveryPublicId: string,
): Promise<ReportDeliveryDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/deliveries/${encodeURIComponent(deliveryPublicId)}/send`,
    { method: "POST" },
  );
}

/** Reject a prepared edition. Only a PREPARED row can be discarded, so this
 * can never rewrite the history of something that already went out. */
export async function discardPreparedDelivery(
  reportPublicId: string,
  deliveryPublicId: string,
): Promise<ReportDeliveryDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/deliveries/${encodeURIComponent(deliveryPublicId)}/discard`,
    { method: "POST" },
  );
}

// ==========================================
// BLOCKS OF A COMPOSED REPORT
// (plan 2026-09-02 fill-with-chat-report-blocks, R4)
// ==========================================
//
// The types come from the generated OpenAPI schemas, never hand-written
// copies: the request bodies are `extra="forbid"` server-side, so a field
// this client invents is a 422 rather than an ignored key. That is also why
// every writer below sends exactly the object it was given.

export type ReportBlockDTO = components["schemas"]["ReportBlockDTO"];
export type ReportBlockListDTO = components["schemas"]["ReportBlockListDTO"];
export type ReportBlockCreate = components["schemas"]["ReportBlockCreate"];
export type ReportBlockUpdate = components["schemas"]["ReportBlockUpdate"];
export type ReportBlockOrder = components["schemas"]["ReportBlockOrder"];
export type ReportBlockPreviewRequest =
  components["schemas"]["ReportBlockPreviewRequest"];
export type ReportBlockPreviewResponse =
  components["schemas"]["ReportBlockPreviewResponse"];
export type ReportBlockPreviewParameters =
  components["schemas"]["ReportBlockPreviewParameters"];
export type ReportBuildOutcomeDTO =
  components["schemas"]["ReportBuildOutcomeDTO"];

/** The block type of a report block. `analysis` is report-only — a dashboard
 *  refresh would be an LLM call per page view. */
export type ReportBlockType = ReportBlockDTO["block_type"];

/** The blocks a data query can fill; everything else is prose or generated. */
export const REPORT_DATA_BLOCK_TYPES: readonly ReportBlockType[] = [
  "kpi",
  "chart",
  "table",
];

export function isReportDataBlock(block: {
  block_type: ReportBlockType;
}): boolean {
  return (REPORT_DATA_BLOCK_TYPES as readonly string[]).includes(
    block.block_type,
  );
}

// --- Pages of a page-format report (plan 2026-09-06-report-page-formats) ----
//
// Types come from the generated OpenAPI schema, never hand-mirrored: a page's
// public id is `page_key` on the wire and the geometry lives on the block, so
// a hand-written copy would be a second source that nothing keeps honest.

export type ReportPageDTO = components["schemas"]["ReportPageDTO"];
export type ReportPageListDTO = components["schemas"]["ReportPageListDTO"];
export type ReportPageCreate = components["schemas"]["ReportPageCreate"];
export type ReportPageOrder = components["schemas"]["ReportPageOrder"];
export type ReportPageRename = components["schemas"]["ReportPageRename"];
export type ReportPageLayoutRequest =
  components["schemas"]["ReportPageLayoutRequest"];
export type PagePlacement = components["schemas"]["PagePlacement"];
export type BlockPlacement = components["schemas"]["BlockPlacement"];

export async function listReportPages(
  reportPublicId: string,
): Promise<ReportPageListDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/pages`);
}

/** The `If-Match` a recipe write sends: the revision the author was looking at.
 *
 *  Plan: docs/plans-pending/2026-09-19-two-editors-in-one-report-overwrite-each-other-silently.md
 *  (step 6). The block editors auto-save the whole form, so an author whose
 *  editor has been open for a minute writes a payload built from a picture
 *  that is already stale, and without this the server accepts it and reverts
 *  whoever edited in between. Both authors get 200 and neither is told.
 *
 *  `undefined` sends nothing, which the server reads as "this write makes no
 *  claim about a version" and allows — that is how the composer's own fills
 *  and every server-side writer keep working.
 *
 *  The shape is `dashboards.ts`'s `ifMatch()`, not a new one. `setReportPageLayout`
 *  below is the exception that stays: its revision travels in the BODY because
 *  the BFF used to forward no caller headers at all, which was the reason the
 *  block writes could carry no precondition in any form (a DELETE has no body
 *  to fold into). The BFF forwards the header since 2026-09-20; the layout
 *  write keeps its body field so an older client is not broken by this. */
function recipeIfMatch(revision?: number): Record<string, string> {
  return revision === undefined ? {} : { "If-Match": String(revision) };
}

/** Take the newer version of the block template this report was built from.
 *
 * The server decides whether this is allowed and says why not, in a sentence
 * meant for the customer: it refuses a report that is not DRAFT, one already
 * on the current version, and a CUSTOMISED one unless `discard_changes` is
 * true. Do not pre-empt those checks here; `template_state` says which
 * question to ASK, the route says what may happen.
 *
 * `discard_changes: true` is irreversible: the report's pages are deleted and
 * the template is re-instantiated, so it must only ever be sent after the
 * customer confirmed exactly that. */
export async function applyTemplateUpdate(
  reportPublicId: string,
  body: { discard_changes?: boolean } = {},
  revision?: number,
): Promise<ReportDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/template-update`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: recipeIfMatch(revision),
  });
}

export async function createReportPage(
  reportPublicId: string,
  body: ReportPageCreate = {},
  revision?: number,
): Promise<ReportPageDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/pages`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: recipeIfMatch(revision),
  });
}

/** Label a page for the author, or clear the label with "" / null. The reader
 *  never sees it: the page number comes from the format's furniture, and the
 *  editor falls back to a positional label, which stays honest after a
 *  reorder in a way a stored "Page 2" would not. */
export async function renameReportPage(
  reportPublicId: string,
  pageKey: string,
  name: string | null,
  revision?: number,
): Promise<ReportPageDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/pages/${encodeURIComponent(pageKey)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ name } satisfies ReportPageRename),
      headers: recipeIfMatch(revision),
    },
  );
}

/** The FULL order, every page exactly once — the server refuses a partial
 *  list rather than guessing where the missing ones go. */
export async function reorderReportPages(
  reportPublicId: string,
  pageKeys: string[],
  revision?: number,
): Promise<ReportPageListDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/pages/order`, {
    method: "PUT",
    body: JSON.stringify({ page_keys: pageKeys } satisfies ReportPageOrder),
    headers: recipeIfMatch(revision),
  });
}

/** Deletes the page AND its blocks — the FK cascades. The caller is expected
 *  to have confirmed with the author first; the count is in the server log. */
export async function deleteReportPage(
  reportPublicId: string,
  pageKey: string,
  revision?: number,
): Promise<ReportPageListDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/pages/${encodeURIComponent(pageKey)}`,
    { method: "DELETE", headers: recipeIfMatch(revision) },
  );
}

/** Bulk geometry for one page. Returns the full block list plus the pages and
 *  the report's NEW revision, so a caller can replace its state in one go
 *  rather than patching it.
 *
 *  `ifMatch` is required, not optional-with-a-default: this write replaces the
 *  placement of every block it lists, so a second author working from an older
 *  picture would revert the first rather than conflict with them. The value is
 *  the `revision` the last block list or layout write returned; the server
 *  answers 409 when it has moved on, and 400 when it is missing.
 *
 *  It travels in the BODY. The BFF folds an `If-Match` header into the body
 *  when one is sent, but the frontend sends the field directly — the same
 *  shape `updateReport` and the review actions already use. */
export async function setReportPageLayout(
  reportPublicId: string,
  pageKey: string,
  placements: PagePlacement[],
  ifMatch: number,
  /** The block(s) the author moved, resized or grew — the server's movers:
   *  a mover pushes what it covers down the page, and a listed block that is
   *  NOT a mover may stand past the bottom (react-grid-layout pushed it there
   *  during the drag) and continues on the next page. Sent only when
   *  non-empty, so a write without movers is the strict write of before,
   *  byte-identical on the wire (ADR-0256). */
  moved: string[] = [],
): Promise<ReportBlockListDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/pages/${encodeURIComponent(pageKey)}/layout`,
    {
      method: "PUT",
      body: JSON.stringify({
        placements,
        ...(moved.length > 0 ? { moved } : {}),
        if_match: ifMatch,
      } satisfies ReportPageLayoutRequest),
    },
  );
}

export type ReportSectionCreate = components["schemas"]["ReportSectionCreate"];
export type ReportSectionInsertResponse =
  components["schemas"]["ReportSectionInsertResponse"];

/** *Insert section*: one page recipe placed on a page, as empty blocks (plan
 *  2026-09-19-insert-section-places-a-whole-section-in-the-report-editor).
 *
 *  A recipe key and a heading, and NO geometry: the server places the whole
 *  section through the same path the composer's `add_section` uses, appending
 *  under what is already on the page and opening a "<page> (continued)" when
 *  it does not fit. The answer is the report AFTER the write, so the caller
 *  needs no second read; `continued_pages` names the pages the server opened,
 *  which is the one thing the author could not have predicted from the menu.
 *
 *  The recipes themselves are not fetched: they are in the generated layout
 *  spec (`PAGE_SECTIONS`), which is where the menu reads them. */
export async function insertReportSection(
  reportPublicId: string,
  pageKey: string,
  recipe: string,
  heading: string,
): Promise<ReportSectionInsertResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/pages/${encodeURIComponent(pageKey)}/sections`,
    {
      method: "POST",
      body: JSON.stringify({ recipe, heading } satisfies ReportSectionCreate),
    },
  );
}

// --- The composer of a page report (plan 2026-09-06-report-page-formats, D2a)
//
// Two calls for one turn. `composeReport` plans and writes nothing to the
// report; `applyReportCompose` writes the plan the author confirmed. The plan
// never travels through the client — the apply sends a conversation id and a
// turn id, and the server reads the plan from that stored turn.

export type ReportComposeRequest = components["schemas"]["ReportComposeRequest"];
export type ReportComposeResponse =
  components["schemas"]["ReportComposeResponse"];
export type ReportComposeApplyRequest =
  components["schemas"]["ReportComposeApplyRequest"];
export type ReportComposeApplyResponse =
  components["schemas"]["ReportComposeApplyResponse"];
export type ReportComposePlaceholderDTO =
  components["schemas"]["ReportComposePlaceholderDTO"];

/** One composer turn: an instruction in, a plan back as sentences.
 *
 *  Nothing on the report changes here — `turn_id` is what makes the plan
 *  appliable, and a turn that answered a question instead of planning returns
 *  none. The `revision` that comes back is what `applyReportCompose` wants as
 *  its `ifMatch`. */
export async function composeReport(
  reportPublicId: string,
  body: ReportComposeRequest,
): Promise<ReportComposeResponse> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/compose`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Write the plan of one turn.
 *
 *  `ifMatch` is required for the same reason `setReportPageLayout` requires
 *  it: a compose moves blocks, so an author working from an older picture
 *  would revert a newer one. 409 means either the report moved (refetch and
 *  ask again) or this turn was already applied — the message says which. */
export async function applyReportCompose(
  reportPublicId: string,
  conversationId: string,
  turnId: string,
  ifMatch: number,
): Promise<ReportComposeApplyResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/compose/apply`,
    {
      method: "POST",
      body: JSON.stringify({
        conversation_id: conversationId,
        turn_id: turnId,
        if_match: ifMatch,
      } satisfies ReportComposeApplyRequest),
    },
  );
}

export async function listReportBlocks(
  reportPublicId: string,
): Promise<ReportBlockListDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/blocks`);
}

export async function createReportBlock(
  reportPublicId: string,
  body: ReportBlockCreate,
  revision?: number,
): Promise<ReportBlockDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/blocks`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: recipeIfMatch(revision),
  });
}

export async function updateReportBlock(
  reportPublicId: string,
  blockPublicId: string,
  body: ReportBlockUpdate,
  revision?: number,
): Promise<ReportBlockDTO> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/blocks/${encodeURIComponent(blockPublicId)}`,
    { method: "PATCH", body: JSON.stringify(body), headers: recipeIfMatch(revision) },
  );
}

export async function deleteReportBlock(
  reportPublicId: string,
  blockPublicId: string,
  revision?: number,
): Promise<{ revision: number }> {
  // 200 with the revision after the delete (plan 2026-09-08-report-editor-
  // is-the-report-itself, A5): a delete is a recipe write, and the canvas
  // keeps its If-Match token from the last answer it saw.
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/blocks/${encodeURIComponent(blockPublicId)}`,
    { method: "DELETE", headers: recipeIfMatch(revision) },
  );
}

/** The whole reading order in one call. Every existing block key must appear
 *  exactly once — the backend refuses a partial list, so this is never a
 *  "move one" endpoint. */
export async function reorderReportBlocks(
  reportPublicId: string,
  blockKeys: string[],
  revision?: number,
): Promise<ReportBlockListDTO> {
  const body: ReportBlockOrder = { block_keys: blockKeys };
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/blocks/order`, {
    method: "PUT",
    body: JSON.stringify(body),
    headers: recipeIfMatch(revision),
  });
}

/**
 * Preview a data block THROUGH THE REPORT'S OWN RENDER PATH (ADR-0202).
 *
 * Not `/composition/preview`: that one runs the dashboard's kernel over the
 * IR directly, while a report block renders as a hook call whose period,
 * timezone, source pin and row limit are the report's. Previewing the wrong
 * one would show the builder numbers the built report cannot reproduce —
 * "the preview is the promise" only holds when both sides run the same code.
 */
export async function previewReportBlock(
  reportPublicId: string,
  body: ReportBlockPreviewRequest,
): Promise<ReportBlockPreviewResponse> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/blocks/preview`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Turn the recipe into a version. 422 names the block that cannot be built;
 *  409 while another build of the same report is still in flight. */
export async function buildReport(
  reportPublicId: string,
  /** Who asked: the Build button, or the editor's View leaving edit mode
   *  (plan 2026-09-08-report-editor-is-the-report-itself, C8). Logged
   *  server-side next to the revisions so a rebuild loop is visible. */
  trigger: "manual" | "view_exit" = "manual",
): Promise<ReportBuildOutcomeDTO> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/build?trigger=${trigger}`, {
    method: "POST",
  });
}

/** What a retry was started for. */
export type RetryReadingsOutcome = {
  /** How many readings are being written again. The notice says this number. */
  retrying: number;
  not_retryable?: number;
};

/** Write the readings that FAILED again, and nothing else.
 *
 * Deliberately not `buildReport`: that route recompiles version 1 from the
 * recipe first, so the readings that DID land would be discarded and the retry
 * would write one of fourteen. And the arithmetic is the point — at ~9.5% per
 * reading, rebuilding all fourteen has a 75% chance of returning a hole, and it
 * re-rolls the thirteen that worked, so the old button usually handed back a
 * report with a *different* reading missing.
 *
 * The body is a FILTER: the backend intersects it with the sections its last
 * build recorded as failed, so sending nothing means "all of them", which is
 * what the button does. Readings that are merely missing a sentence are NOT
 * retried — re-rolling one has about a 1 in 10 chance of coming back empty,
 * which is a worse trade than leaving a gap the reader can fill in Edit. */
export async function retryReadings(
  reportPublicId: string,
  sectionIds?: string[],
): Promise<RetryReadingsOutcome> {
  return apiJson(`/reports/${encodeURIComponent(reportPublicId)}/retry-readings`, {
    method: "POST",
    body: JSON.stringify(sectionIds?.length ? { section_ids: sectionIds } : {}),
  });
}

// --- What an agent deep dive would pick right now ---------------------------
//
// Plan 2026-09-09-reports-intelligence-one-context-scoped-blocks, Besluit 8:
// the panel shows what the SELECTION would do without writing a word, so an
// author can judge a scope before a build. No LLM; it does cost a facts read
// on BigQuery, which is why the backend rate-limits it per account (10/min)
// and caches the run context for ten minutes.
//
export type DeepDivePreviewItem = components["schemas"]["DeepDivePreviewItem"];
export type DeepDiveSelectionPreview = components["schemas"]["DeepDiveSelectionPreview"];

/** Which findings this deep-dive block would pick if the report were built
 *  now. Addressed by BLOCK KEY, not by the block's public id: the backend
 *  route is `/blocks/{block_key}/preview-selection` (the selection is a
 *  property of the recipe, and the recipe speaks block keys).
 *
 *  No body — the block carries the whole question. 429 when the account has
 *  clicked more than ten times in a minute, 422 with a sentence when the
 *  block cannot be previewed at all; both reach the caller as an `ApiError`
 *  whose `detail` is the sentence to show. */
export async function previewDeepDiveSelection(
  reportPublicId: string,
  blockKey: string,
): Promise<DeepDiveSelectionPreview> {
  return apiJson(
    `/reports/${encodeURIComponent(reportPublicId)}/blocks/${encodeURIComponent(blockKey)}/preview-selection`,
    { method: "POST" },
  );
}
